/**
 * Edit Ship Page
 * ==============
 * صفحة تعديل شيب مع نظام المتطلبات الديناميكي
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTenantAuth } from '../../context/TenantAuthContext';
import { useToast } from '../../context/ToastContext';
import '../shared/PageStyles.css';

const I = {
  back: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="m15 18-6-6 6-6"/></svg>,
  check: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>,
  upload: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  map: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  link: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>,
  loading: <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="spin"><circle cx="12" cy="12" r="10" strokeDasharray="32" strokeLinecap="round"/></svg>,
};

const groupIcons = {
  'info-circle': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="12" y1="16" x2="12" y2="12"/><line x1="12" y1="8" x2="12.01" y2="8"/></svg>,
  'map-marker-alt': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="10" r="3"/><path d="M12 21.7C17.3 17 20 13 20 10a8 8 0 1 0-16 0c0 3 2.7 7 8 11.7z"/></svg>,
  'phone': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>,
  'clock': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>,
  'user': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>,
  'file-alt': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>,
  'upload': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>,
  'image': <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/><circle cx="8.5" cy="8.5" r="1.5"/><polyline points="21 15 16 10 5 21"/></svg>,
};

const EXCLUDED_GROUPS = ['owner_info'];

const EditShipPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { apiCall } = useTenantAuth();
  const { success, error: showError } = useToast();
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [categories, setCategories] = useState([]);
  const [requirements, setRequirements] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({});
  const [errors, setErrors] = useState({});
  const [activeGroup, setActiveGroup] = useState(0);
  const [shipData, setShipData] = useState(null);
  const [allWaterTypes, setAllWaterTypes] = useState([]);
  const [selectedWaterTypes, setSelectedWaterTypes] = useState([]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [catsRes, reqsRes, shipRes, wtAllRes, wtShipRes] = await Promise.all([
        apiCall('/tenant/ship-requirements/categories'),
        apiCall('/tenant/ship-requirements/requirements'),
        apiCall(`/tenant/ship-requirements/ships/${id}`),
        apiCall('/ships/water-types/all'),
        apiCall(`/ships/${id}/water-types`),
      ]);
      
      setCategories(catsRes || []);
      setShipData(shipRes);
      setAllWaterTypes(wtAllRes?.items || []);
      setSelectedWaterTypes((wtShipRes?.items || []).map(i => i.code));
      
      const filteredReqs = (reqsRes.requirements || []).filter(r => !EXCLUDED_GROUPS.includes(r.group_key));
      const filteredGroups = (reqsRes.groups || []).filter(g => !EXCLUDED_GROUPS.includes(g.key));
      
      setRequirements(filteredReqs);
      setGroups(filteredGroups);
      
      // Initialize form data with ship values and requirement data
      const initialData = {};
      const reqData = shipRes.requirement_data || {};
      
      (reqsRes.requirements || []).forEach(req => {
        // First check requirement_data, then ship basic fields
        if (reqData[req.field_key] && reqData[req.field_key].value !== null) {
          // Parse JSON value if needed
          let val = reqData[req.field_key].value;
          if (typeof val === 'string') {
            try {
              val = JSON.parse(val);
            } catch (e) {
              // Keep as string
            }
          }
          initialData[req.field_key] = val !== null && val !== undefined ? String(val) : '';
        } else {
          // Fallback to basic ship fields
          const shipValue = shipRes[req.field_key];
          initialData[req.field_key] = shipValue !== null && shipValue !== undefined ? String(shipValue) : (req.default_value || '');
        }
      });
      
      setFormData(initialData);
      setSelectedCategory(shipRes.category_id || '');
      
    } catch (err) {
      showError('فشل تحميل البيانات');
      navigate('/tenant/ships');
    } finally {
      setLoading(false);
    }
  }, [apiCall, id, showError, navigate]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleChange = (fieldKey, value) => {
    setFormData(prev => ({ ...prev, [fieldKey]: value }));
    if (errors[fieldKey]) {
      setErrors(prev => ({ ...prev, [fieldKey]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    requirements.forEach(req => {
      if (req.is_required) {
        const value = formData[req.field_key];
        if (!value || (typeof value === 'string' && !value.trim())) {
          newErrors[req.field_key] = `${req.field_name_ar} مطلوب`;
        }
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showError('الرجاء تعبئة جميع الحقول المطلوبة بشكل صحيح');
      return;
    }

    setSubmitting(true);
    try {
      // Collect files to upload
      const filesToUpload = [];
      
      // Filter data and collect files
      const reqData = Object.entries(formData)
        .filter(([key, value]) => {
          // Collect files for upload
          if (key.endsWith('_file') && value instanceof File) {
            filesToUpload.push({ fieldKey: key.replace('_file', ''), file: value });
            return false;
          }
          // Skip internal fields
          if (key.endsWith('_uploading') || key.endsWith('_file') || key.endsWith('_url')) {
            return false;
          }
          if (value instanceof File) {
            filesToUpload.push({ fieldKey: key, file: value });
            return false;
          }
          return true;
        })
        .map(([field_key, value]) => ({
          field_key,
          value: typeof value === 'object' ? JSON.stringify(value) : String(value || '')
        }));

      // Update ship data
      await apiCall(`/tenant/ship-requirements/ships/${id}`, {
        method: 'PUT',
        body: JSON.stringify({
          category_id: selectedCategory || null,
          requirements: reqData
        })
      });

      // Upload files
      for (const { fieldKey, file } of filesToUpload) {
        try {
          const uploadForm = new FormData();
          uploadForm.append('file', file);
          uploadForm.append('ship_id', id);
          uploadForm.append('document_type', fieldKey);

          const token = localStorage.getItem('watrly_tenant_token');
          await fetch('http://localhost:8002/api/v1/tenant/uploads/ship-document', {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`
            },
            body: uploadForm
          });
        } catch (uploadErr) {
          console.error('File upload failed:', fieldKey, uploadErr);
        }
      }

      // Save water types
      try {
        await apiCall(`/ships/${id}/water-types`, {
          method: 'PUT',
          body: JSON.stringify({ codes: selectedWaterTypes }),
        });
      } catch (wtErr) {
        console.error('Failed to save water types:', wtErr);
      }

      success('تم تحديث الشيب بنجاح');
      navigate('/tenant/ships');
    } catch (err) {
      showError(err.message || 'فشل تحديث الشيب');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const value = formData[field.field_key] || '';
    const error = errors[field.field_key];
    const commonProps = {
      id: field.field_key,
      value,
      onChange: (e) => handleChange(field.field_key, e.target.value),
      placeholder: field.placeholder_ar || field.placeholder || '',
      className: error ? 'input-error' : '',
      disabled: submitting
    };

    switch (field.field_type) {
      case 'select':
        const options = field.options || [];
        return (
          <select {...commonProps}>
            <option value="">-- اختر --</option>
            {options.map((opt, i) => {
              const val = typeof opt === 'object' ? opt.value : opt;
              const label = typeof opt === 'object' ? (opt.label_ar || opt.label_en || opt.value) : opt;
              return (
                <option key={val ?? i} value={val}>{label}</option>
              );
            })}
          </select>
        );

      case 'textarea':
        return <textarea {...commonProps} rows={4} />;

      case 'number':
        return <input type="number" step="any" {...commonProps} />;

      case 'time':
        return <input type="time" {...commonProps} />;

      case 'checkbox':
        return (
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={value === 'true' || value === true}
              onChange={(e) => handleChange(field.field_key, e.target.checked ? 'true' : 'false')}
              disabled={submitting}
            />
            <span>{field.placeholder_ar || 'نعم'}</span>
          </label>
        );

      case 'file':
        // Get existing file from images or requirement_data
        const existingImage = shipData?.images?.find(img => img.image_type === field.field_key);
        const existingFileUrl = value && value.startsWith('/uploads') ? value : null;
        const hasExistingFile = existingImage || existingFileUrl || (value && !value.includes('اختر'));
        const fileUrl = existingImage?.image_url || existingFileUrl;
        const isImage = fileUrl && /\.(jpg|jpeg|png|gif|webp)$/i.test(fileUrl);
        
        return (
          <div className="file-upload">
            {hasExistingFile && (
              <div className="existing-file">
                {isImage && fileUrl ? (
                  <img 
                    src={fileUrl.startsWith('http') ? fileUrl : `http://localhost:8002${fileUrl}`} 
                    alt={field.field_name_ar} 
                    className="file-preview" 
                  />
                ) : (
                  <div className="file-icon-preview">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="40" height="40">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                    </svg>
                  </div>
                )}
                <span className="file-name">{value || 'ملف مرفوع'}</span>
                {fileUrl && (
                  <a 
                    href={fileUrl.startsWith('http') ? fileUrl : `http://localhost:8002${fileUrl}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="view-file-btn"
                    onClick={(e) => e.stopPropagation()}
                  >
                    عرض الملف
                  </a>
                )}
              </div>
            )}
            <input
              type="file"
              id={field.field_key}
              accept={field.file_types ? field.file_types.split(',').map(t => `.${t}`).join(',') : 'image/*,.pdf'}
              multiple={field.max_files > 1}
              onChange={(e) => {
                const files = Array.from(e.target.files || []);
                if (files.length > 0) {
                  const file = files[0];
                  // Store file for upload
                  handleChange(`${field.field_key}_file`, file);
                  handleChange(field.field_key, file.name);
                }
              }}
              disabled={submitting}
              style={{ display: 'none' }}
            />
            <label htmlFor={field.field_key} className="file-upload-label">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                <polyline points="17 8 12 3 7 8"/>
                <line x1="12" y1="3" x2="12" y2="15"/>
              </svg>
              <span>{hasExistingFile ? 'استبدال الملف' : 'اختر ملف...'}</span>
            </label>
            {field.file_types && (
              <small className="file-hint">
                الأنواع المسموحة: {field.file_types}
              </small>
            )}
          </div>
        );

      case 'date':
        return <input type="date" {...commonProps} />;

      case 'url':
        return <input type="url" {...commonProps} dir="ltr" style={{ textAlign: 'left' }} />;

      default:
        return <input type="text" {...commonProps} />;
    }
  };

  if (loading) {
    return (
      <div className="page">
        <div className="page-loading"><div className="spinner" /></div>
      </div>
    );
  }

  return (
    <div className="page add-ship-page">
      <div className="page-header">
        <div className="page-header__title">
          <button className="back-btn" onClick={() => navigate('/tenant/ships')}>
            {I.back}
          </button>
          <div>
            <h1>تعديل الشيب</h1>
            <p>{shipData?.name_ar || shipData?.name || ''} - {shipData?.code || ''}</p>
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="ship-form">
        <div className="form-sidebar">
          {/* Category Selection */}
          {categories.length > 0 && (
            <div className="sidebar-section">
              <h3>نوع الشيب</h3>
              <select
                value={selectedCategory}
                onChange={(e) => setSelectedCategory(e.target.value)}
                className="category-select"
              >
                <option value="">-- اختر النوع --</option>
                {categories.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.name_ar || cat.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Groups Navigation */}
          <div className="sidebar-section">
            <h3>الأقسام</h3>
            <div className="groups-nav">
              {groups.map((group, idx) => (
                <button
                  key={group.key}
                  type="button"
                  className={`group-btn ${activeGroup === idx ? 'active' : ''}`}
                  onClick={() => setActiveGroup(idx)}
                >
                  {groupIcons[group.icon] || groupIcons['info-circle']}
                  <span>{group.name_ar}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="form-main">
          {/* Active Group Fields */}
          {groups[activeGroup] && (
            <div className="form-content">
              <div className="form-section-title">{groups[activeGroup].name_ar}</div>
              <div className="form-grid">
                {groups[activeGroup].fields
                  .filter(field => {
                    if ((field.field_key === 'opening_time' || field.field_key === 'closing_time') && 
                        formData.working_hours_type === '24_hours') {
                      return false;
                    }
                    return true;
                  })
                  .map(field => (
                  <div
                    key={field.field_key}
                    className={`form-group ${field.field_type === 'textarea' || field.field_type === 'file' ? 'full-width' : ''}`}
                  >
                    <label htmlFor={field.field_key}>
                      {field.is_required && <span className="required">*</span>}
                      {field.field_name_ar}
                    </label>
                    {renderField(field)}
                    {errors[field.field_key] && (
                      <span className="error-text">{errors[field.field_key]}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Water Type Selector */}
          {allWaterTypes.length > 0 && (
            <div className="water-type-section" style={{ marginTop: '16px' }}>
              <div className="section-title">أنواع المياه المتوفرة في الشيب</div>
              <p className="section-hint">حدد أنواع المياه التي يقدمها هذا الشيب — ستُستخدم لتصفية الطلبات تلقائياً</p>
              <div className="water-type-grid">
                {allWaterTypes.map(wt => {
                  const isSelected = selectedWaterTypes.includes(wt.code);
                  return (
                    <label
                      key={wt.code}
                      className={`water-type-card ${isSelected ? 'selected' : ''}`}
                      style={{ '--wt-color': wt.color_code || '#3b82f6' }}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={(e) => {
                          setSelectedWaterTypes(prev =>
                            e.target.checked
                              ? [...prev, wt.code]
                              : prev.filter(c => c !== wt.code)
                          );
                        }}
                        disabled={submitting}
                      />
                      <div className="water-type-card__body">
                        <strong>{wt.name_ar || wt.name_en}</strong>
                        {wt.description_ar && <small>{wt.description_ar}</small>}
                      </div>
                      {isSelected && (
                        <span className="water-type-check">
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" width="14" height="14"><polyline points="20 6 9 17 4 12"/></svg>
                        </span>
                      )}
                    </label>
                  );
                })}
              </div>
            </div>
          )}

          {/* Navigation & Submit */}
          <div className="form-actions">
            <div className="nav-buttons">
              {activeGroup > 0 && (
                <button type="button" className="btn btn--secondary" onClick={() => setActiveGroup(activeGroup - 1)}>
                  السابق
                </button>
              )}
              {activeGroup < groups.length - 1 && (
                <button type="button" className="btn btn--primary" onClick={() => setActiveGroup(activeGroup + 1)}>
                  التالي
                </button>
              )}
            </div>
            
            <button type="submit" className="btn btn--primary submit-btn" disabled={submitting}>
              {submitting ? I.loading : I.check}
              <span>{submitting ? 'جاري الحفظ...' : 'حفظ التعديلات'}</span>
            </button>
          </div>
        </div>
      </form>

      <style>{`
        .add-ship-page .back-btn {
          width: 40px;
          height: 40px;
          border: none;
          background: var(--tenant-bg);
          border-radius: 10px;
          cursor: pointer;
          color: var(--tenant-text-muted);
          display: flex;
          align-items: center;
          justify-content: center;
          margin-left: 16px;
        }
        .add-ship-page .back-btn:hover {
          background: var(--tenant-border);
          color: var(--tenant-text);
        }
        .add-ship-page .back-btn svg {
          width: 20px;
          height: 20px;
        }
        .add-ship-page .page-header__title {
          display: flex;
          align-items: center;
        }
        .ship-form {
          display: flex;
          gap: 24px;
          margin-top: 24px;
        }
        .form-sidebar {
          width: 280px;
          flex-shrink: 0;
        }
        .sidebar-section {
          background: var(--tenant-card-bg);
          border-radius: 12px;
          padding: 16px;
          margin-bottom: 16px;
        }
        .sidebar-section h3 {
          font-size: 14px;
          font-weight: 600;
          color: var(--tenant-text);
          margin: 0 0 12px;
        }
        .category-select {
          width: 100%;
          padding: 10px 12px;
          border: 1px solid var(--tenant-border);
          border-radius: 8px;
          font-size: 14px;
          background: var(--tenant-bg);
        }
        .groups-nav {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }
        .group-btn {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 10px 12px;
          border: none;
          background: transparent;
          border-radius: 8px;
          cursor: pointer;
          color: var(--tenant-text-muted);
          font-size: 13px;
          text-align: right;
          transition: all 0.2s;
        }
        .group-btn:hover {
          background: var(--tenant-bg);
          color: var(--tenant-text);
        }
        .group-btn.active {
          background: var(--tenant-primary);
          color: white;
        }
        .group-btn svg {
          width: 18px;
          height: 18px;
          flex-shrink: 0;
        }
        .form-main {
          flex: 1;
          background: var(--tenant-card-bg);
          border-radius: 16px;
          padding: 24px;
        }
        .form-section-title {
          font-size: 18px;
          font-weight: 600;
          color: var(--tenant-text);
          margin-bottom: 20px;
          padding-bottom: 12px;
          border-bottom: 1px solid var(--tenant-border);
        }
        .form-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }
        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }
        .form-group.full-width {
          grid-column: span 2;
        }
        .form-group label {
          font-size: 13px;
          font-weight: 500;
          color: var(--tenant-text);
        }
        .form-group .required {
          color: #ef4444;
          margin-left: 4px;
        }
        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 10px 14px;
          border: 1px solid var(--tenant-border);
          border-radius: 8px;
          font-size: 14px;
          background: var(--tenant-bg);
          transition: all 0.2s;
        }
        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: var(--tenant-primary);
          background: white;
        }
        .form-group .input-error {
          border-color: #ef4444;
        }
        .error-text {
          font-size: 12px;
          color: #ef4444;
        }
        .checkbox-label {
          display: flex;
          align-items: center;
          gap: 8px;
          cursor: pointer;
        }
        .checkbox-label input {
          width: 18px;
          height: 18px;
        }
        .file-upload {
          display: flex;
          flex-direction: column;
          gap: 8px;
        }
        .existing-file {
          display: flex;
          align-items: center;
          gap: 12px;
          padding: 12px;
          background: var(--tenant-bg);
          border-radius: 8px;
          border: 1px solid var(--tenant-border);
          flex-wrap: wrap;
        }
        .file-preview {
          width: 80px;
          height: 80px;
          object-fit: cover;
          border-radius: 6px;
          border: 1px solid var(--tenant-border);
        }
        .file-icon-preview {
          width: 60px;
          height: 60px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: rgba(59, 130, 246, 0.1);
          border-radius: 8px;
          color: var(--tenant-primary);
        }
        .file-name {
          font-size: 13px;
          color: var(--tenant-text-muted);
          flex: 1;
          word-break: break-all;
        }
        .view-file-btn {
          padding: 6px 12px;
          background: var(--tenant-primary);
          color: white;
          border-radius: 6px;
          font-size: 12px;
          text-decoration: none;
          white-space: nowrap;
        }
        .view-file-btn:hover {
          background: #2563eb;
        }
        .file-upload-label {
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 24px;
          border: 2px dashed var(--tenant-border);
          border-radius: 10px;
          cursor: pointer;
          transition: all 0.2s;
          background: var(--tenant-bg);
        }
        .file-upload-label:hover {
          border-color: var(--tenant-primary);
          background: rgba(59, 130, 246, 0.05);
        }
        .file-upload-label svg {
          width: 32px;
          height: 32px;
          color: var(--tenant-primary);
        }
        .file-upload-label span {
          font-size: 14px;
          color: var(--tenant-text-muted);
        }
        .file-hint {
          color: var(--tenant-text-muted);
          font-size: 12px;
          text-align: center;
        }
        .form-actions {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-top: 24px;
          padding-top: 20px;
          border-top: 1px solid var(--tenant-border);
        }
        .nav-buttons {
          display: flex;
          gap: 8px;
        }
        .submit-btn {
          display: flex;
          align-items: center;
          gap: 8px;
        }
        .submit-btn svg {
          width: 18px;
          height: 18px;
        }
        @media (max-width: 768px) {
          .ship-form {
            flex-direction: column;
          }
          .form-sidebar {
            width: 100%;
          }
          .form-grid {
            grid-template-columns: 1fr;
          }
          .form-group.full-width {
            grid-column: span 1;
          }
        }
      `}</style>
    </div>
  );
};

export default EditShipPage;
